import React, { useContext, useState, useEffect } from 'react';
import { default as Form } from '../../components/connectedForm';
import EcSalesListingLineWorkspace from './EcSalesListingLineWorkspace';
import ecSalesListingByUserQuery from '../../graphql/queries/vat/EcSalesListingByUserQuery.graphql';
import {
  IEcSalesListingByUserQueryResult,
  IEcSalesListingByUserQueryVariables,
  IFinancialPeriod,
} from '../../graphql/schema/p4types';
import WorkspaceContext from '../../components/Context/WorkspaceContext';
import HeaderBlock from '../../components/connectedForm/HeaderBlock';
import { useQuery } from '@apollo/client';
import { useComponentQueryErrorHandling } from '../../utils/parseErrorResponse';
import { formatDateTime } from '../../utils/DateHelper';
import { Maybe } from 'graphql/jsutils/Maybe';
import { useHistory, useLocation } from 'react-router';
import QueryDropdown from '../../components/connectedForm/QueryDropdown';
import FinancialPeriod from '../../graphql/entities/company/FinancialPeriod';
import BlurTextField from '../../components/connectedForm/BlurTextField';

interface IEcSalesListingHeaderProps {
  fromFinancialPeriod: Maybe<IFinancialPeriod>;
  setFromFinancialPeriod: (period: IFinancialPeriod | null) => void;
  toFinancialPeriod: Maybe<IFinancialPeriod>;
  setToFinancialPeriod: (period: IFinancialPeriod | null) => void;
  lastGenerationDate: string;
}

const EcSalesListingHeader = ({
  fromFinancialPeriod,
  setFromFinancialPeriod,
  toFinancialPeriod,
  setToFinancialPeriod,
  lastGenerationDate,
}: IEcSalesListingHeaderProps) => {
  const workspaceContext = useContext(WorkspaceContext);
  const history = useHistory();
  const location = useLocation<{ previousLocation: string }>();

  return (
    <>
      <HeaderBlock
        extraBtns={workspaceContext.workspaceActionButtons}
        name="EC Sales Reporting"
        noSaveOption={true}
        noDeleteOption={true}
        onCancel={() => {
          const previousUrl = location?.state?.previousLocation;
          if (history && previousUrl) {
            history.goBack();
          }
        }}
      />
      <Form.Fieldset cols={1 / 3}>
        <QueryDropdown
          data-testid={'ec-sales-from'}
          label="From Period"
          {...FinancialPeriod.dropdown}
          value={fromFinancialPeriod}
          onChange={setFromFinancialPeriod}
          labelCols={6 / 12}
          inputCols={6 / 12}
        />
        <QueryDropdown
          data-testid={'ec-sales-to'}
          label="To Period"
          {...FinancialPeriod.dropdown}
          value={toFinancialPeriod}
          onChange={setToFinancialPeriod}
          labelCols={6 / 12}
          inputCols={6 / 12}
        />
        <BlurTextField
          data-testid={'ec-sales-listing-generation-date'}
          value={lastGenerationDate ? formatDateTime(lastGenerationDate) : ''}
          label="Last Generation Date"
          readOnly={true}
          labelCols={6 / 12}
          inputCols={6 / 12}
        />
      </Form.Fieldset>
    </>
  );
};

const EcSalesListingForm = () => {
  const [generationDate, setGenerationDate] = useState<Maybe<string>>('');
  const [jobId, setJobId] = useState<string>('0');
  const [fromFinancialPeriod, setFromFinancialPeriod] = useState<Maybe<IFinancialPeriod>>(null);
  const [toFinancialPeriod, setToFinancialPeriod] = useState<Maybe<IFinancialPeriod>>(null);
  const handleQueryError = useComponentQueryErrorHandling();

  const { data: ecSalesListingInfo } = useQuery<IEcSalesListingByUserQueryResult, IEcSalesListingByUserQueryVariables>(
    ecSalesListingByUserQuery,
    {
      fetchPolicy: 'network-only',
      onError: handleQueryError,
    }
  );

  useEffect(() => {
    if (ecSalesListingInfo?.data) {
      const data = ecSalesListingInfo.data;
      setGenerationDate(data.updatedAt || '');
      setJobId(data.jobId || '0');
      setToFinancialPeriod(data.financialPeriodTo as IFinancialPeriod);
      setFromFinancialPeriod(data.financialPeriodFrom as IFinancialPeriod);
    }
  }, [ecSalesListingInfo]);

  return (
    <>
      <EcSalesListingLineWorkspace
        viewScreenName="/Customer/EcSalesListingLine/Workspace"
        includeGenericSearch={false}
        jobId={jobId}
        fromFinancialPeriod={fromFinancialPeriod}
        toFinancialPeriod={toFinancialPeriod}
        ignoreContainerWidth={true}
        embeddedView={true}
        setJobId={setJobId}
      >
        <EcSalesListingHeader
          fromFinancialPeriod={fromFinancialPeriod}
          setFromFinancialPeriod={setFromFinancialPeriod}
          toFinancialPeriod={toFinancialPeriod}
          setToFinancialPeriod={setToFinancialPeriod}
          lastGenerationDate={generationDate || ''}
        />
      </EcSalesListingLineWorkspace>
    </>
  );
};

export default EcSalesListingForm;
